import { Button } from '@progress/kendo-react-buttons';
import { Breadcrumb } from '@progress/kendo-react-layout';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { hideLoading, showLoading } from '../../actions/api';
import { showNotification } from '../../actions/notifications';
import { DocumentKind, StorageItem, StorageItemFull } from '../../constants/document';
import NotificationsTypes from '../../constants/notification';
import documentsService from '../../services/documentsService';
import { forceDownload, getDocumentKindByContentType } from '../../utils';
import styles from '../documents-viewer/DocumentsViewer.module.css';

interface BreadcrumbData {
  id: string;
  text: string;
  iconClass?: string;
}

const buildBreadcrumbData = (path: string | undefined): BreadcrumbData[] => {
  if (!path) return [];
  const segments = path.split('/').filter(Boolean);
  const lastIndex = segments.length - 1;
  return segments.map((segment, idx) => ({
    id: String(idx),
    text: segment,
    iconClass: idx === 0 ? 'k-i-home' : idx === lastIndex ? undefined : 'k-i-folder',
  }));
};

const SharedLinkFileViewer = () => {
  const dispatch = useDispatch();
  const [storageFullItem, setStorageFullItem] = useState<StorageItemFull | undefined | null>(undefined);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breadcrumbData = useMemo(
    () => buildBreadcrumbData(storageFullItem?.path),
    [storageFullItem?.path],
  );

  const getData = async () => {
    try {
      dispatch(showLoading());
      var linkCode = window.location.pathname.split('/').pop() ?? '';
      const response = await documentsService.openSharedFileLink(linkCode);
      if (response) {
        setStorageFullItem(response);
      }
    } catch (error: any) {
      setStorageFullItem(null);
      dispatch(
        showNotification({
          message: 'Requested document is not available, contact with the department which gave the link.',
          type: NotificationsTypes.Error,
        }),
      );
      setTimeout(() => {
        window.location.href = `/?${new Date().getTime()}`;
      }, 2000);
    } finally {
      dispatch(hideLoading());
    }
  };

  const onDownloadDocument = async (item: StorageItem | undefined | null) => {
    if (!item?.url) return;
    try {
      dispatch(showLoading());
      await forceDownload(item.url, item.text ?? '');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('[SharedLinkFileViewer.onDownloadDocument] failed:', error);
      dispatch(
        showNotification({
          message: `Download failed: ${error?.message ?? 'unknown error'}`,
          type: NotificationsTypes.Error,
        }),
      );
    } finally {
      dispatch(hideLoading());
    }
  };

  const renderHeader = () => (
    <div className={styles['viewer-header']}>
      <Breadcrumb
        data={breadcrumbData}
        className={styles['viewer-breadcrumb']}
        textField="text"
        iconClassField="iconClass"
        ariaLabel="Document path"
      />
      <Button
        icon="download"
        className="btn-action-add"
        title="Download file"
        onClick={() => onDownloadDocument(storageFullItem)}
      />
    </div>
  );

  const getDefaultRender = () => {
    if (!storageFullItem) return null;
    switch (getDocumentKindByContentType(storageFullItem.contentType)) {
      case DocumentKind.image:
        return (
          <>
            {renderHeader()}
            <div className={styles['viewer-content']}>
              <img
                src={storageFullItem.url}
                alt={storageFullItem.text}
                style={{ maxHeight: '100%', maxWidth: '100%' }}
              />
            </div>
          </>
        );
      case DocumentKind.pdf:
        return (
          <>
            {renderHeader()}
            <div className={styles['viewer-content']}>
              <iframe
                src={storageFullItem.url}
                title={storageFullItem.text}
                style={{ height: '100%', width: '100%', border: 'none' }}
              />
            </div>
          </>
        );
      default:
        return (
          <>
            {renderHeader()}
            <div className={styles['viewer-content']}>
              <h3>Preview Not Available</h3>
            </div>
          </>
        );
    }
  };

  const getLoadingDocument = () => <h3 style={{ alignSelf: 'center' }}>Loading document</h3>;

  return (
    <div className={styles['viewer-wrapper']}>
      {!storageFullItem ? getLoadingDocument() : getDefaultRender()}
    </div>
  );
};

export default SharedLinkFileViewer;
